import { DUEL_STAT_PROFILES, duelLevelSkill, type DuelLevel } from '../battle/aiSkill';
import { characterAiProfile } from '../battle/characterAiProfile';
import { attackInterval, characterSize } from '../characters/stats';
import type { CharacterDef } from '../characters/types';
import {
  arenaCombatSignature,
  calibrationTable,
  characterCombatSignature,
  levelCombatSignature,
  signatureStands,
} from './arenaCalibration';
import { arenaForGame } from './definition';
import { duelForm, duelHitPoints, healthLogOdds } from './battle';
import { curseLogOdds } from './curse';
import type { GameContext, GameState, Piece } from './types';
import type { ArenaCalibration } from './arenaCalibration';

const evaluations = new WeakMap<
  GameContext,
  {
    characters: Map<string, string>;
    /** Level stamps (`level|id`), worked out the first time a level's table is read. */
    levels: Map<string, string>;
    /** By `arena id|level`. */
    tables: Map<string, ArenaCalibration>;
  }
>();
/** Short-lived synchronous evaluation snapshot; never store this on a game or editor. */
export function withCardEvaluation(ctx: GameContext): GameContext {
  if (evaluations.has(ctx)) return ctx;
  const copy = { ...ctx },
    characters = new Map(
      Object.values(ctx.roster).map((c) => [c.id, characterCombatSignature(c, true)]),
    );
  const tables = new Map<string, ArenaCalibration>();
  for (const arena of ctx.definition?.arenas ?? []) {
    const tiles = ctx.definition!.tilesets.find((t) => t.id === arena.tileset);
    if (!tiles) continue;
    const signature = arenaCombatSignature(arena, tiles, true);
    for (const table of ctx.definition!.arenaCalibration ?? []) {
      const key = `${arena.id}|${table.level ?? 'expert'}`;
      if (table.arena === signature && !tables.has(key)) tables.set(key, table);
    }
  }
  evaluations.set(copy, { characters, levels: new Map(), tables });
  return copy;
}

export const cardLogit = (p: number): number => Math.log(Math.max(1e-6, p) / Math.max(1e-6, 1 - p));
export const cardLogistic = (x: number): number => 1 / (1 + Math.exp(-x));
/** One shared response, fitted on 750 mixed-level Arena fights; Expert remains unchanged. */
export const CARD_LEVEL_RESPONSE = 3.7125;

/** Property estimate, not a claim of measured Arena accuracy for arbitrary edited content. */
function strength(
  c: CharacterDef,
  foe: CharacterDef,
  level: DuelLevel,
  foeLevel: DuelLevel,
): number {
  // A shapeshifter fights as its opponent, body and weapon (D-56); two of them hand to hand.
  if (c.attack.type === 'mimic' && foe.attack.type !== 'mimic') c = foe;
  else if (foe.attack.type === 'mimic' && c.attack.type !== 'mimic') foe = c;
  const own = level === 'expert' ? undefined : c.combat?.levels?.[level];
  const other = foeLevel === 'expert' ? undefined : foe.combat?.levels?.[foeLevel];
  const stat = DUEL_STAT_PROFILES[level],
    defence = DUEL_STAT_PROFILES[foeLevel];
  const ai = characterAiProfile(c, duelLevelSkill(level));
  const attack = c.attack;
  const type = attack.type === 'mimic' ? 'melee' : attack.type;
  const protection = Math.min(
    0.75,
    (foe.combat?.protection ?? 0) * defence.protection * (other?.protection ?? 1),
  );
  const effect = Math.min(
    1,
    (c.attack.effectChance ?? 1) * stat.effectChance * (own?.effectChance ?? 1),
  );
  let output =
    (attack.damage * stat.damage * (own?.damage ?? 1)) /
    (attackInterval(attack) * stat.attackInterval * (own?.attackInterval ?? 1) + ai.cadence);
  if (type === 'melee' && foe.attack.type !== 'melee') output *= 0.5;
  if (type === 'burst') output *= 0.8;
  if (type === 'burn')
    output *= 1.5 * Math.sqrt((((attack.reach ?? 2) / 2) * (attack.duration ?? 3)) / 3);
  if (type === 'projectile') output *= (1 / (1 + ai.aimSpread * 3)) * (0.7 + 0.3 * ai.lead);
  output *=
    effect *
    (1 - protection) *
    (1 - (foe.combat?.resistances?.[attack.damageType ?? 'physical'] ?? 0));
  output *= Math.sqrt(
    Math.max(
      0.001,
      ((c.combat?.speed ?? 1) *
        ai.moveScale *
        characterSize(c.tier, c.movement.type === 'fly').speed) /
        6,
    ),
  );
  output *= (1 / (1 + ai.reaction)) * (0.8 + 0.2 * ai.notice);
  return Math.max(1e-9, c.hitPoints * stat.hitPoints * (own?.hitPoints ?? 1) * output);
}

function cardTable(
  ctx: GameContext,
  state?: GameState,
  level: DuelLevel = 'expert',
): ArenaCalibration | undefined {
  if (!ctx.definition) return undefined;
  const arena = state
    ? arenaForGame(ctx.definition, state)
    : {
        arena: ctx.definition.arenas.find((x) => x.id === ctx.definition!.defaultArenaId)!,
        tileset: undefined,
      };
  if (!arena.arena) return undefined;
  const tiles = arena.tileset ?? ctx.definition.tilesets.find((t) => t.id === arena.arena.tileset);
  if (!tiles) return undefined;
  const snapshot = evaluations.get(ctx);
  return snapshot
    ? snapshot.tables.get(`${arena.arena.id}|${level}`)
    : calibrationTable(ctx.definition, arenaCombatSignature(arena.arena, tiles, true), level);
}

/** A pair's measured odds at a level (Expert's unless named), where the game's table stands. */
function measured(
  ctx: GameContext,
  a: CharacterDef,
  d: CharacterDef,
  state?: GameState,
  level: DuelLevel = 'expert',
): number | undefined {
  const table = cardTable(ctx, state, level),
    snapshot = evaluations.get(ctx);
  const stands = (c: CharacterDef) => {
    const stamp = table?.characters[c.id];
    if (stamp === undefined) return false;
    if (level !== 'expert') {
      if (!snapshot || c !== ctx.roster[c.id])
        return stamp === levelCombatSignature(c, level, true);
      const key = `${level}|${c.id}`;
      let current = snapshot.levels.get(key);
      if (current === undefined)
        snapshot.levels.set(key, (current = levelCombatSignature(c, level, true)));
      return stamp === current;
    }
    const current = c === ctx.roster[c.id] ? snapshot?.characters.get(c.id) : undefined;
    return stamp === current || signatureStands(c, stamp, true);
  };
  if (!table || !stands(a) || !stands(d)) return undefined;
  if (a.id === d.id) return 0.5;
  const ids = [a.id, d.id].sort();
  const p = table.probabilities[ids.join('|')];
  return p === undefined ? undefined : a.id === ids[0] ? p : 1 - p;
}

function expertEstimate(
  ctx: GameContext,
  a: CharacterDef,
  d: CharacterDef,
  state?: GameState,
): number {
  const ratio = strength(a, d, 'expert', 'expert') / strength(d, a, 'expert', 'expert');
  const refs = cardTable(ctx, state)?.reference,
    ra = refs?.[a.id],
    rd = refs?.[d.id];
  const anchor = ra && rd ? measured(ctx, ra, rd, state) : undefined;
  if (anchor !== undefined && ra && rd) {
    const before = strength(ra, rd, 'expert', 'expert') / strength(rd, ra, 'expert', 'expert');
    return cardLogistic(cardLogit(anchor) + 1.5 * Math.log(ratio / before));
  }
  return cardLogistic(1.5 * Math.log(ratio));
}

export function cardPairModel(
  ctx: GameContext,
  state: GameState,
  a: Piece,
  d: Piece,
): { probability: number; source: 'measured' | 'estimated' } {
  const ac = ctx.roster[a.character]!,
    dc = ctx.roster[d.character]!;
  const al = ctx.cardLevels?.[a.faction] ?? 'expert',
    dl = ctx.cardLevels?.[d.faction] ?? 'expert';
  // Two seats at one level read the level's own measured odds where the game has them (phase D),
  // as Roll does; the property model's step from Expert then has nothing left to estimate.
  const own = al === dl && al !== 'expert' ? measured(ctx, ac, dc, state, al) : undefined;
  const base = own ?? measured(ctx, ac, dc, state);
  const expertRatio = strength(ac, dc, 'expert', 'expert') / strength(dc, ac, 'expert', 'expert');
  const levelRatio =
    own !== undefined ? expertRatio : strength(ac, dc, al, dl) / strength(dc, ac, dl, al);
  const x = state.pendingBattle?.x ?? d.x,
    y = state.pendingBattle?.y ?? d.y;
  // A shapeshifter's health is its copy's, a share of the body it copies (D-56). Light above
  // whole weighs as the board's odds weigh it (`SQUARE_LIGHT`), so Expert agrees with the Arena's.
  const condition =
    healthLogOdds(
      duelHitPoints(ctx, state, a, d, x, y) / ctx.roster[duelForm(ctx, a, d)]!.hitPoints,
    ) -
    healthLogOdds(
      duelHitPoints(ctx, state, d, a, x, y) / ctx.roster[duelForm(ctx, d, a)]!.hitPoints,
    );
  const logOdds =
    cardLogit(base ?? expertEstimate(ctx, ac, dc, state)) +
    CARD_LEVEL_RESPONSE * Math.log(levelRatio / expertRatio) +
    condition +
    curseLogOdds(a, d);
  return {
    probability: cardLogistic(Math.max(-14, Math.min(14, logOdds))),
    source:
      own !== undefined || (base !== undefined && al === 'expert' && dl === 'expert')
        ? 'measured'
        : 'estimated',
  };
}

/** Stable Expert rating against this game's roster, independent of current opponent or wounds. */
export function cardExpertRating(ctx: GameContext, character: string): number {
  ctx = withCardEvaluation(ctx);
  const c = ctx.roster[character];
  if (!c) return 0;
  const opponents = Object.values(ctx.roster).filter((d) => d.id !== c.id);
  if (!opponents.length) return 50;
  const total = opponents.reduce(
    (sum, d) => sum + (measured(ctx, c, d) ?? expertEstimate(ctx, c, d)),
    0,
  );
  return Math.round((100 * total) / opponents.length);
}
